import type { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface StaticMetricCardAltProps {
  title: string;
  value: string;
  change: string;
  changeLabel: string;
  icon: LucideIcon;
  variant?: "success" | "warning" | "error" | "info" | "default";
  tooltip?: string;
}

const variantStyles = {
  success: {
    background: "bg-success-100", // soft green background
    icon: "text-success-600", // success-600 icon color
  },
  warning: {
    background: "bg-warning-100", // soft orange background
    icon: "text-warning-600", // warning-600 icon color
  },
  error: {
    background: "bg-error-100", // soft red background
    icon: "text-error-600", // error-600 icon color
  },
  info: {
    background: "bg-teal-100", // soft teal background
    icon: "text-teal-600", // teal-600 icon color
  },
  default: {
    background: "bg-white", // white background
    icon: "text-neutral-600", // neutral icon color
  },
};

export function StaticMetricCardAlt({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  variant = "success",
  tooltip,
}: StaticMetricCardAltProps) {
  const styles = variantStyles[variant];

  return (
    <Card className="overflow-hidden border-0">
      {/* Colored Top Section - IMPORTANT: Use className with template literal, NOT inline style */}
      <div className={`relative h-24 p-4 ${styles.background}`}>
        <div className="flex items-center justify-between h-full">
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-1.5">
              <p className="text-neutral-700 text-sm font-medium">{title}</p>
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-neutral-500 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-neutral-900 text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className={`${styles.icon}`}>
            <Icon className="h-10 w-10" strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* White Bottom Section */}
      <div className="p-4 bg-white border-t border-border">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-emerald-600">{change}</span>
          <span className="text-sm text-neutral-500">{changeLabel}</span>
        </div>
      </div>
    </Card>
  );
}
